import { ReactNode } from 'react';
import { FormLabelProps as ChakraFormLabelProps, FormLabel as ChakraFormLabel, forwardRef } from '@chakra-ui/react';
import Text from '../Text/Text';
import Tooltip, { TooltipProps } from '../Tooltip/Tooltip';
import Box from '../Box/Box';
import Icon from '../Icon/Icon';

export interface FormLabelProps extends ChakraFormLabelProps {
  badge?: ReactNode;
  children?: string;
  infoTooltipLabel?: string;
  infoTooltipProps?: TooltipProps;
  maxLength?: number;
  valueLength?: number;
  withCounter?: boolean;
}

const FormLabel = forwardRef<FormLabelProps, 'label'>((props, ref) => {
  const { badge, infoTooltipLabel, infoTooltipProps, maxLength, valueLength, withCounter, ...rest } = props;

  const showLabel = rest.children || !!infoTooltipLabel || (withCounter && maxLength);

  if (!showLabel) {
    return null;
  }

  const counterColor = maxLength && valueLength && valueLength > maxLength ? 'input/text/error' : 'text/secondary';

  return (
    <Box alignItems="center" display="flex" gap="4" marginBlockEnd="4">
      <ChakraFormLabel
        optionalIndicator={
          <Text as="span" color="text/secondary" fontSize="2" fontWeight="normal" marginInlineStart="4">
            (optional)
          </Text>
        }
        requiredIndicator={null}
        {...rest}
        ref={ref}
      />

      {!!infoTooltipLabel && (
        <Tooltip label={infoTooltipLabel} placement="right" {...infoTooltipProps}>
          <Icon color="icon/tertiary" name="Info" size="16" />
        </Tooltip>
      )}
      <Box as="span" alignSelf="flex-end" marginInlineStart="auto">
        {badge}
      </Box>
      {!badge && withCounter && maxLength && (
        <Text
          as="span"
          color={counterColor}
          marginInlineStart="auto"
          size="2"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {valueLength}/{maxLength}
        </Text>
      )}
    </Box>
  );
});

export default FormLabel;
